/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Pressable,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { Portal } from '@gorhom/portal';

import IconView from '@airasia-phoenix/react-native/components/IconView';
import StickyBottomBarView from '@airasia-phoenix/react-native/components/StickyBottomBarView';
import { AALocaleProvider } from '@airasia-phoenix/react-native/utils/AALocaleContext';
import { getColor, pd } from '@airasia-phoenix/react-native/utils/utils';
import { DatePicker } from '@airasia-phoenix/react-native/widgets';
import type {
  DatePickerWithBottomSheetProps,
  DatePickerWithBottomSheetRefs,
} from '@airasia-phoenix/react-native/widgets';

import { DROP_DOWN_PORTAL_HOST_NAME } from '../../configs/constants';
import Close from '@move-travel/react-native-phoenix-icons/Close';

const MobileWebDatePickerBottomSheet = forwardRef<
  DatePickerWithBottomSheetRefs,
  DatePickerWithBottomSheetProps
>(
  (
    { bottomSheetViewProps, datePickerProps, stickyBottomBarViewProps },
    ref,
  ) => {
    const { height } = useWindowDimensions();
    const [visible, setVisible] = useState(false);
    const isVisibleRef = useRef(false);

    const handleClose = useCallback(() => {
      if (!isVisibleRef.current) return;

      isVisibleRef.current = false;
      setVisible(false);
      bottomSheetViewProps?.onChange?.(-1);
      bottomSheetViewProps?.onBottomSheetClose?.();
    }, [bottomSheetViewProps]);

    const handleOpen = useCallback(() => {
      if (isVisibleRef.current) return;

      isVisibleRef.current = true;
      setVisible(true);
      bottomSheetViewProps?.onChange?.(0);
    }, [bottomSheetViewProps]);

    useImperativeHandle(ref, () => ({
      toggleDatePicker() {
        if (isVisibleRef.current) {
          handleClose();
          return;
        }

        handleOpen();
      },
    }));

    if (!visible) return null;

    return (
      <Portal hostName={DROP_DOWN_PORTAL_HOST_NAME}>
        <View
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 1000,
          }}
          testID="mobile-web-date-picker-bottom-sheet"
        >
          <Pressable
            style={{
              flex: 1,
              backgroundColor:
                bottomSheetViewProps?.backdropProps?.backgroundColor ??
                'rgba(44, 43, 47, 0.8)',
            }}
            onPress={handleClose}
            testID="mobile-web-date-picker-backdrop"
          />
          <View
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: bottomSheetViewProps?.height ?? height * 0.8,
              backgroundColor: getColor(pd.comp.bottom_sheet.container.colour),
              borderTopLeftRadius: 14,
              borderTopRightRadius: 14,
              overflow: 'hidden',
            }}
          >
            <AALocaleProvider>
              <View style={{ flex: 1, minHeight: 0 }}>
                <View
                  style={{
                    flexDirection: 'row',
                    // marginHorizontal: pd.ref.space.spacing40.px,
                    marginTop: pd.ref.space.spacing40.px,
                    zIndex: 1,
                    // width: '10%',
                    top: 15,
                    left: 20,
                  }}
                >
                  <TouchableOpacity
                    style={{
                      marginRight: pd.ref.space.spacing20.px,
                    }}
                    onPress={handleClose}
                    testID="mobile-web-date-picker-close"
                  >
                    <IconView
                      height={24}
                      width={24}
                      tint={getColor(pd.comp.bottom_sheet.icon.colour)}
                    >
                      <Close />
                    </IconView>
                  </TouchableOpacity>
                </View>

                <View style={{ flex: 1, minHeight: 0 }}>
                  <DatePicker isInsideBottomSheetView {...datePickerProps} />
                </View>
                <StickyBottomBarView {...stickyBottomBarViewProps} />
              </View>
            </AALocaleProvider>
          </View>
        </View>
      </Portal>
    );
  },
);

MobileWebDatePickerBottomSheet.displayName = 'MobileWebDatePickerBottomSheet';

export default MobileWebDatePickerBottomSheet;
